import { type ChangeEvent, type ReactNode, useCallback, useId, useRef, useState } from 'react';
import type { CommentAttachmentDto } from '@inference.engine/workspace-api-client/web';
import { CommentInput } from '@/entities/project-comment';
import type { MentionOption } from '@/entities/project-comment';
import { useCommentAttachments } from '@/entities/project-comment/lib/useCommentAttachments';
import { PendingAttachmentChips } from '@/entities/project-comment/ui/CommentInput/PendingAttachmentChips';
import { notificationService } from '@/lib/services/notification.service';
import {
  DEFAULT_ANNOTATION_TOOL,
  useActiveComposerActions,
  useAnnotationDraft,
  useAnnotationSolo,
  useAnnotationTool,
  useCommentsScope,
  useHasActiveComposer,
  useIsActiveComposer,
} from '../model/comments.store';
import { useMediaPlayerOptional } from '../model/media-player.store';
import { buildAnnotationPayload } from '../model/buildAnnotationPayload';
import { requestCommentAttachmentUpload } from '../api/comments.api';
import type { AnnotatedCommentMediaKind, Annotation } from '../model/annotation';
import { useVideoComposerAnnotationSeconds } from '../lib/useVideoComposerAnnotationSeconds';
import { CommentInputActionsBar } from './CommentInputActionsBar';
import { VideoCommentComposerTimestampChip } from './VideoCommentComposerTimestampChip';
import { useTranslation } from 'react-i18next';

export type { AnnotatedCommentMediaKind };

export interface AnnotatedCommentInputProps {
  placeholder?: string;
  initialContent?: Record<string, unknown>;
  initialAttachments?: CommentAttachmentDto[];
  // `undefined` = no draft; `null` = edit explicitly cleared; otherwise the built payload.
  onSave: (
    content: Record<string, unknown>,
    annotation: Annotation | null | undefined,
    attachmentAssetIds: string[],
  ) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  isEditing?: boolean;
  assetKind?: AnnotatedCommentMediaKind;
  showAnnotationToolbar?: boolean;
  /**
   * Whether this composer captures an annotation on save (shapes, and for
   * video the playhead timestamp). Reply composers opt out: replies pin to
   * their parent comment, never to their own frame/annotation.
   */
  supportsAnnotation?: boolean;
  /**
   * When true (default), focusing the composer clears any soloed annotation —
   * correct for top-level threads where the user is starting fresh. Reply
   * composers must opt out: the parent comment IS the solo, so clearing it
   * would unmount the reply input mid-focus.
   */
  clearSoloOnFocus?: boolean;
  /** Agents offered by the `@` mention dropdown (brief composer only). */
  mentionOptions?: MentionOption[];
  composerPrefix?: ReactNode;
  /**
   * Id of whatever the pending comment was just anchored to. When it becomes a new non-null
   * value the composer takes the caret, so selecting a section lets the user type at once.
   */
  focusKey?: string | null;
  isDefaultComposer?: boolean;
  disabled?: boolean;
  onFocus?: () => void;
}

export function AnnotatedCommentInput({
  placeholder,
  initialContent,
  initialAttachments,
  onSave,
  onCancel,
  submitLabel,
  isEditing = false,
  assetKind = 'image',
  showAnnotationToolbar = true,
  supportsAnnotation = true,
  clearSoloOnFocus = true,
  mentionOptions,
  composerPrefix: composerPrefixProp,
  focusKey,
  isDefaultComposer = false,
  disabled = false,
  onFocus,
}: AnnotatedCommentInputProps) {
  const { t } = useTranslation('projects');

  const composerId = useId();
  const isActiveComposer = useIsActiveComposer(composerId);
  const { setActiveComposer, clearActiveComposer } = useActiveComposerActions();
  const { draftShapes, capturedTimestamp, cancelDraft } = useAnnotationDraft();
  const { clearSolo } = useAnnotationSolo();
  const { tool, selectTool } = useAnnotationTool();
  // Optional: brief comments render this composer without a MediaPlayerProvider.
  // Asset surfaces always have one, so their behavior is unchanged.
  const { getCurrentTime, mediaElement } = useMediaPlayerOptional();
  const { projectId, targetType, targetId } = useCommentsScope();
  const hasCommentScope = !!projectId && !!targetId;

  // Route upload through workspace-api so the resulting assetId is claimed for
  // this user + project/target — backend rejects attach() of any unclaimed id.
  const requestUpload = useCallback(
    async (file: File) => {
      if (!hasCommentScope) {
        throw new Error('Cannot upload attachments before comment scope is ready.');
      }
      return requestCommentAttachmentUpload(
        projectId,
        { targetType, targetId },
        {
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
        },
      );
    },
    [hasCommentScope, projectId, targetType, targetId],
  );

  const attachments = useCommentAttachments(requestUpload, initialAttachments);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [composerResetKey, setComposerResetKey] = useState(0);

  const handleSave = useCallback(
    async (content: Record<string, unknown>) => {
      if (attachments.hasInFlight) return;
      const annotation = supportsAnnotation
        ? buildAnnotationPayload(draftShapes, {
            isEditing,
            assetKind,
            capturedTimestamp,
            getCurrentTime,
          })
        : undefined;
      await onSave(content, annotation, attachments.assetIds);
      attachments.reset();
      cancelDraft();
      // Exit drawing mode — otherwise `useIsComposing` stays true (because
      // `tool !== null`) and the soloed annotation overlay is suppressed on
      // the comment that was just saved.
      selectTool(null);
      clearActiveComposer();
    },
    [
      draftShapes,
      isEditing,
      assetKind,
      capturedTimestamp,
      getCurrentTime,
      supportsAnnotation,
      onSave,
      attachments,
      cancelDraft,
      selectTool,
      clearActiveComposer,
    ],
  );

  const handleCancel = useCallback(() => {
    setComposerResetKey((k) => k + 1);
    attachments.reset();
    cancelDraft();
    selectTool(null);
    clearActiveComposer();
    onCancel?.();
  }, [attachments, cancelDraft, selectTool, clearActiveComposer, onCancel]);

  const handleOpenFilePicker = useCallback(() => {
    if (!hasCommentScope) {
      notificationService.showError(
        t('commentOnFile.annotatedCommentInput.attachmentsUnavailable'),
      );
      return;
    }
    fileInputRef.current?.click();
  }, [hasCommentScope, t]);

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        void attachments.add(files);
      }
      e.target.value = '';
    },
    [attachments],
  );

  // `onFocus` bubbles from any descendant (TipTap editor, action buttons, popover
  // trigger) — when the composer becomes active we (a) clear any soloed annotation
  // (unless the caller opted out — see `clearSoloOnFocus`) and (b) prime the
  // default annotation tool *only* if nothing is selected, so a refocus doesn't
  // clobber the user's prior pick. The tool is global store state, so composers
  // that don't own an annotation toolbar (replies, non-root edits) must NOT
  // prime it — otherwise focusing a reply lights up the root composer's toolbar.
  const handleFocus = useCallback(() => {
    setActiveComposer(composerId);
    if (clearSoloOnFocus) clearSolo();
    if (showAnnotationToolbar && tool === null) selectTool(DEFAULT_ANNOTATION_TOOL);
    onFocus?.();
  }, [
    composerId,
    setActiveComposer,
    clearSoloOnFocus,
    clearSolo,
    showAnnotationToolbar,
    tool,
    selectTool,
    onFocus,
  ]);

  const hasActiveComposer = useHasActiveComposer();
  const showComposerPrefix =
    isEditing || isActiveComposer || (isDefaultComposer && !hasActiveComposer);

  const composerVideoSeconds = useVideoComposerAnnotationSeconds({
    assetKind,
    capturedTimestamp,
    getCurrentTime,
    mediaElement,
  });

  return (
    <div className="w-full min-w-0" onFocus={handleFocus}>
      <CommentInput
        disabled={disabled}
        key={composerResetKey}
        placeholder={placeholder}
        initialContent={initialContent}
        submitLabel={submitLabel}
        submitDisabled={attachments.hasInFlight}
        mentionOptions={mentionOptions}
        // The video timestamp keeps the inline float: it is one line tall by construction and
        // belongs on the text line. An anchor chip goes on its own row instead — it can be as
        // long as a section name, and a float that outgrows the editor's line gets clipped by
        // the scroll box, taking its clear control with it.
        composerPrefix={
          supportsAnnotation && composerVideoSeconds != null ? (
            <VideoCommentComposerTimestampChip seconds={composerVideoSeconds} />
          ) : undefined
        }
        composerAnchor={showComposerPrefix ? composerPrefixProp : undefined}
        focusKey={focusKey}
        topContent={
          attachments.attachments.length > 0 ? (
            <PendingAttachmentChips
              attachments={attachments.attachments}
              onRemove={attachments.remove}
              disabled={false}
            />
          ) : undefined
        }
        onSave={handleSave}
        leftActions={(actions) => (
          <CommentInputActionsBar
            assetKind={assetKind}
            onInsertText={actions.insertText}
            onOpenFilePicker={handleOpenFilePicker}
            onCancel={handleCancel}
            canAttach={hasCommentScope && !actions.isSubmitting && !attachments.isAtLimit}
            showCancel={isEditing}
            isSubmitting={actions.isSubmitting}
            showAnnotationToolbar={showAnnotationToolbar}
            isToolActive={showAnnotationToolbar && isActiveComposer}
            showDraftActions={isEditing}
          />
        )}
      />
      <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileInputChange} />
    </div>
  );
}
